import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  LoadingSkeleton,
  ProgressBar,
  StatCard,
  StatsGrid,
} from '@nexora/design-system';
import {
  useClosingDashboard,
  useTogglePeriodLock,
} from './useClosing';
import {
  STATUS_MODULO_LABEL,
  STATUS_PERIODO_LABEL,
  MODULO_ROTULO,
  type ModuloFechamentoStatus,
  type FechamentoPeriodoStatus,
  type DashboardModulo,
} from './types';
import { formatCount, formatDateTime, formatPercent } from '../../shared/utils/format';

/* ═══════════════════════════════════════════════════════════════════════════
   Painel de Controle do Fechamento Mensal — Matriz Brasil
   ───────────────────────────────────────────────────────────────────────────
   Contrato consumido:

   GET /closing/periods/dashboard?mes=&ano= → ClosingDashboard
     { period: { id, ano, mes, status, masterPeriodLock, label, fechadoEm,
                 assinadoPor, assinadoEm },
       completionPercent, concluidos, totalModulos,
       modulos: [{ modulo (fiscal|inventory|financial|accounting), status,
                   observacao, concluidoEm }],
       moduloStatusCounts, criticalPendingIssues: [{ id, titulo, descricao,
                   modulo, severidade, criadoEm, resolvido }],
       pendingCriticalCount }

   POST /closing/periods/:id/lock { ativar, por } → FechamentoPeriodo
     (liga/desliga o Master Period Lock)

   Portado de base-html, slug `erpone_painel_de_controle_fechamento_mensal`:
   do protótipo vieram o cabeçalho do período com badge de status e barra de
   "Overall Completion", os quatro cartões de Module Status (Fiscal, Inventory,
   Financial, Accounting), o controle Master Period Lock (lado direito) e o
   painel vermelho de Critical Pending Issues.

   Não vieram (não existem no contrato): os botões "Recalculate Balances",
   "Global Validation" e "Trial Balance" — o backend não expõe endpoints de
   recálculo/validação global a partir do painel, e o Trial Balance vive no
   módulo accounting. A barra lateral de navegação do protótipo é o shell da
   aplicação (não faz parte da página).
   ═══════════════════════════════════════════════════════════════════════════ */

const MODULO_BADGE_VARIANT: Record<ModuloFechamentoStatus, 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
  PENDENTE: 'warning',
  EM_ANDAMENTO: 'info',
  CONCLUIDO: 'success',
  BLOQUEADO: 'error',
  NAO_APLICAVEL: 'neutral',
};

const PERIODO_BADGE_VARIANT: Record<FechamentoPeriodoStatus, 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
  ABERTO: 'info',
  EM_ANDAMENTO: 'warning',
  CONCILIACAO: 'warning',
  VALIDACAO: 'info',
  FECHADO: 'success',
  CANCELADO: 'error',
};

/** Mês por extenso — PT-BR, ex.: "Outubro 2024". */
function mesLabel(mes: number, ano: number): string {
  const meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
  ];
  const nome = meses[mes - 1] ?? String(mes);
  return `${nome} ${ano}`;
}

function ModuloCard({ mod }: { mod: DashboardModulo }): JSX.Element {
  const rotulo = MODULO_ROTULO[mod.modulo] ?? mod.modulo;
  return (
    <Card variant="outlined" padding="md" className="closing-module-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <span className="font-headline-sm text-headline-sm text-primary">{rotulo}</span>
      </div>
      <div style={{ marginTop: 'auto' }}>
        <Badge variant={MODULO_BADGE_VARIANT[mod.status]} size="sm">
          {STATUS_MODULO_LABEL[mod.status]}
        </Badge>
        {mod.observacao && (
          <p className="font-body-sm text-body-sm text-on-surface-variant mt-sm" style={{ marginBottom: 0 }}>
            {mod.observacao}
          </p>
        )}
        {mod.concluidoEm && (
          <p className="font-label-sm text-label-sm text-on-surface-variant mt-xs" style={{ marginBottom: 0 }}>
            Concluído em {formatDateTime(mod.concluidoEm)}
          </p>
        )}
      </div>
    </Card>
  );
}

export function ClosingDashboardPage(): JSX.Element {
  const hoje = new Date();
  const anoInicial = hoje.getFullYear();
  const mesInicial = hoje.getMonth() + 1;

  const [ano, setAno] = useState(anoInicial);
  const [mes, setMes] = useState(mesInicial);

  const { data, isLoading, error, refetch } = useClosingDashboard({ mes, ano });
  const lockMutation = useTogglePeriodLock(data?.period.id);

  const period = data?.period;
  const modulos = data?.modulos ?? [];
  const issues = data?.criticalPendingIssues ?? [];
  const issuesNaoResolvidas = issues.filter((i) => !i.resolvido);
  const podeTravar = modulos.length > 0 && modulos.every((m) => m.status === 'CONCLUIDO' || m.status === 'NAO_APLICAVEL');

  const handleToggleLock = async (): Promise<void> => {
    if (!period) return;
    try {
      await lockMutation.mutateAsync({ ativar: !period.masterPeriodLock });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao alterar o Master Period Lock.';
      window.alert(msg);
    }
  };

  return (
    <div className="closing-dashboard-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <p className="font-label-sm text-label-sm text-on-surface-variant mb-1 uppercase tracking-wider">
            Fechamento Mensal / Matriz Brasil
          </p>
          <h1 className="font-display-lg text-display-lg text-primary m-0">Painel de Controle</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Status dos módulos, travamento do período e pendências críticas da Matriz Brasil.
          </p>
        </div>
        <div className="flex gap-sm items-end">
          <div style={{ flex: '0 0 110px' }}>
            <Input
              label="Mês"
              type="number"
              min={1}
              max={12}
              value={String(mes)}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (v >= 1 && v <= 12) setMes(v);
              }}
              inputSize="sm"
            />
          </div>
          <div style={{ flex: '0 0 110px' }}>
            <Input
              label="Ano"
              type="number"
              min={2000}
              value={String(ano)}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (v >= 2000) setAno(v);
              }}
              inputSize="sm"
            />
          </div>
        </div>
      </div>

      {error && !isLoading && (
        <Card variant="outlined" className="mb-lg" padding="1.5rem">
          <p className="m-0" style={{ fontWeight: 600, color: '#ba1a1a' }}>Erro ao carregar o painel</p>
          <p className="m-0" style={{ fontSize: '0.875rem', color: '#74777f', marginTop: '0.25rem' }}>
            {error instanceof Error ? error.message : 'Erro desconhecido'}
          </p>
          <Button variant="primary" size="sm" onClick={() => refetch()} style={{ marginTop: '0.75rem' }}>
            Tentar novamente
          </Button>
        </Card>
      )}

      {isLoading && (
        <div>
          <LoadingSkeleton count={3} height="96px" borderRadius="12px" />
          <LoadingSkeleton count={4} height="120px" borderRadius="12px" />
        </div>
      )}

      {!isLoading && data && (
        <>
          {/* Header / Status Overview */}
          <Card variant="outlined" padding="lg" className="mb-lg">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-lg">
              <div className="flex-1">
                <div className="flex items-center gap-md mb-sm">
                  <h2 className="font-headline-md text-headline-md text-primary m-0">
                    {period ? mesLabel(period.mes, period.ano) : 'Período'}
                  </h2>
                  {period && (
                    <Badge variant={PERIODO_BADGE_VARIANT[period.status]} size="md" dot>
                      {STATUS_PERIODO_LABEL[period.status]}
                    </Badge>
                  )}
                </div>
                <div className="mt-md">
                  <div className="flex justify-between font-label-md text-label-md text-on-surface-variant mb-xs">
                    <span>Conclusão geral</span>
                    <span className="font-bold text-primary">{formatPercent(data.completionPercent / 100, 0)}</span>
                  </div>
                  <ProgressBar value={data.completionPercent ?? 0} max={100} />
                  <p className="font-label-sm text-label-sm text-on-surface-variant mt-xs" style={{ marginBottom: 0 }}>
                    {data.concluidos} de {data.totalModulos} módulos concluídos
                  </p>
                </div>
              </div>
              {period?.assinadoEm && (
                <div className="font-label-sm text-label-sm text-on-surface-variant">
                  Assinado por {period.assinadoPor ?? '—'} em {formatDateTime(period.assinadoEm)}
                </div>
              )}
            </div>
          </Card>

          {/* KPIs */}
          <StatsGrid columns={4} className="mb-lg">
            <StatCard
              label="Módulos concluídos"
              value={`${data.concluidos}/${data.totalModulos}`}
              icon={<span className="material-symbols-outlined" aria-hidden="true">check_circle</span>}
              variant="success"
            />
            <StatCard
              label="Módulos pendentes"
              value={formatCount(data.totalModulos - data.concluidos)}
              icon={<span className="material-symbols-outlined" aria-hidden="true">pending</span>}
              variant="warning"
            />
            <StatCard
              label="Pendências críticas"
              value={formatCount(data.pendingCriticalCount)}
              icon={<span className="material-symbols-outlined" aria-hidden="true">warning</span>}
              variant={data.pendingCriticalCount > 0 ? 'error' : 'success'}
            />
            <StatCard
              label="Master Period Lock"
              value={period?.masterPeriodLock ? 'Ativo' : 'Inativo'}
              icon={<span className="material-symbols-outlined" aria-hidden="true">lock</span>}
              variant={period?.masterPeriodLock ? 'info' : 'success'}
            />
          </StatsGrid>

          {/* Main grid: Module Status (8) + Master Lock & Issues (4) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-lg">
            <div className="lg:col-span-8 space-y-lg">
              <h3 className="font-headline-md text-headline-md text-on-surface border-b border-outline-variant pb-xs">
                Module Status
              </h3>
              {modulos.length === 0 ? (
                <Card variant="outlined" padding="lg">
                  <p className="m-0" style={{ fontSize: '0.875rem' }}>
                    Nenhum módulo registrado para este período. Abra um novo período para iniciar o fechamento.
                  </p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
                  {modulos.map((m) => (
                    <ModuloCard key={m.modulo} mod={m} />
                  ))}
                </div>
              )}
            </div>

            <div className="lg:col-span-4 space-y-lg flex flex-col">
              {/* Master Period Lock */}
              <Card variant="outlined" padding="lg" className="closing-lock-card">
                <h3 className="font-headline-sm text-headline-sm text-on-surface mb-xs">Master Period Lock</h3>
                <p className="font-body-sm text-body-sm text-on-surface-variant mb-md" style={{ minHeight: '2.5rem' }}>
                  {period?.masterPeriodLock
                    ? 'Período travado: nenhuma postagem adicional é permitida sem autorização.'
                    : podeTravar
                      ? 'Todos os módulos concluídos — o período pode ser travado.'
                      : 'Não é possível travar o período enquanto houver módulos sem status "Closed".'}
                </p>
                <Button
                  variant={period?.masterPeriodLock ? 'outline' : 'primary'}
                  size="md"
                  disabled={!period || (!period.masterPeriodLock && !podeTravar) || lockMutation.isPending}
                  onClick={handleToggleLock}
                  style={{ width: '100%' }}
                >
                  {lockMutation.isPending
                    ? 'Processando...'
                    : period?.masterPeriodLock
                      ? 'Destravar período'
                      : `Travar ${period ? mesLabel(period.mes, period.ano) : 'período'}`}
                </Button>
              </Card>

              {/* Critical Pending Issues */}
              <Card variant="outlined" padding="0" className="closing-issues-card">
                <CardHeader
                  className="px-md pt-md"
                >
                  <h3 className="font-headline-sm text-headline-sm" style={{ margin: 0, color: '#93000a' }}>
                    Critical Pending Issues
                    {data.pendingCriticalCount > 0 && (
                      <span className="font-body-sm text-body-sm ml-sm">({formatCount(data.pendingCriticalCount)})</span>
                    )}
                  </h3>
                </CardHeader>
                <div style={{ padding: '0.5rem', overflowY: 'auto', maxHeight: '340px' }}>
                  {issuesNaoResolvidas.length === 0 ? (
                    <div style={{ padding: '1.5rem 1rem', textAlign: 'center' }}>
                      <p className="font-body-sm text-body-sm text-on-surface-variant m-0">
                        Nenhuma pendência crítica no momento.
                      </p>
                    </div>
                  ) : (
                    issuesNaoResolvidas.map((issue) => (
                      <div
                        key={issue.id}
                        style={{
                          background: '#ffffff',
                          padding: '0.75rem',
                          borderRadius: '0.5rem',
                          border: `1px solid ${issue.severidade === 'ALTA' ? '#ba1a1a' : '#f2b8b5'}`,
                          marginBottom: '0.25rem',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                            style={{ fontSize: '18px', color: '#ba1a1a', marginTop: '2px' }}
                          >
                            warning
                          </span>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <p className="font-label-md text-label-md font-bold m-0" style={{ color: '#191c1e' }}>
                                {issue.titulo}
                              </p>
                              <Badge variant={issue.severidade === 'ALTA' ? 'error' : issue.severidade === 'MEDIA' ? 'warning' : 'info'} size="sm">
                                {issue.severidade ?? 'INFO'}
                              </Badge>
                            </div>
                            {issue.descricao && (
                              <p className="font-body-sm text-body-sm text-on-surface-variant m-0" style={{ marginTop: '0.125rem' }}>
                                {issue.descricao}
                              </p>
                            )}
                            {issue.modulo && (
                              <p className="font-label-sm text-label-sm text-on-surface-variant m-0" style={{ marginTop: '0.125rem' }}>
                                Módulo: {MODULO_ROTULO[issue.modulo] ?? issue.modulo}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}